import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";

import { ConnectivityBadge, connectivityState } from "../../components/devices/ConnectivityBadge";
import { devicesApi } from "../../lib/api/devices";
import { invalidatePolicyViews } from "../../lib/policyQueries";
import { LIVE_REFRESH_INTERVAL_MS, useLiveNow } from "../../lib/useLiveNow";
import { Alert } from "../../components/ui/Alert";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { ConfirmDialog } from "../../components/ui/ConfirmDialog";
import { FormField } from "../../components/ui/FormField";
import { Input } from "../../components/ui/Input";
import { Modal } from "../../components/ui/Modal";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchInput } from "../../components/ui/SearchInput";
import { EmptyState, ErrorState, LoadingState } from "../../components/ui/State";
import { type Column, Table } from "../../components/ui/Table";
import { useToast } from "../../components/ui/Toast";
import { ApiError } from "../../lib/api";
import { employeesApi, type Employee, type PresenceEvent } from "../../lib/api/employees";
import { formatDateTime } from "../../lib/format";
import { formatMessage, t } from "../../lib/i18n";
import { useLocale } from "../../lib/useLocale";

function translateError(err: unknown): string {
  if (err instanceof ApiError) {
    const localized = t(`errors.${err.code}`, "");
    if (localized) return localized;
    return err.message || t("errors.generic");
  }
  return t("errors.network");
}

function sourceLabel(source: string): string {
  if (!source) return "—";
  return t(`employees.source.${source}`, source);
}

interface EmployeeFormState {
  employee_id: string;
  first_name: string;
  last_name: string;
  phone: string;
}

const EMPTY_CREATE_FORM: EmployeeFormState = {
  employee_id: "",
  first_name: "",
  last_name: "",
  phone: "",
};

export function EmployeesListPage() {
  useLocale();
  const now = useLiveNow();
  const qc = useQueryClient();
  const { push: pushToast } = useToast();
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<EmployeeFormState>(EMPTY_CREATE_FORM);

  const [editRow, setEditRow] = useState<Employee | null>(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", phone: "" });

  const [historyRow, setHistoryRow] = useState<Employee | null>(null);
  const [pendingForceEntry, setPendingForceEntry] = useState<Employee | null>(null);
  const [pendingForceExit, setPendingForceExit] = useState<Employee | null>(null);

  const query = useQuery({
    queryKey: ["employees", search],
    queryFn: () => employeesApi.list({ search: search || undefined }),
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });

  const needsDeviceFallback = query.data?.some((row) =>
    row.device_count > 0 && typeof row.is_online !== "boolean" &&
    typeof row.online_device_count !== "number" && row.last_seen_at === undefined) ?? false;
  const connectivity = useQuery({
    queryKey: ["employees", "connectivity-devices"],
    queryFn: () => devicesApi.listPage({ limit: 200 }),
    enabled: needsDeviceFallback,
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: "always",
  });
  function employeeConnectivity(row: Employee): { isOnline?: boolean; lastSeenAt?: string | null } {
    if (typeof row.is_online === "boolean") return { isOnline: row.is_online, lastSeenAt: row.last_seen_at };
    if (typeof row.online_device_count === "number") return { isOnline: row.online_device_count > 0, lastSeenAt: row.last_seen_at };
    if (row.last_seen_at !== undefined) return { lastSeenAt: row.last_seen_at };
    const devices = connectivity.data?.results.filter((d) => d.employee?.id === row.id) ?? [];
    const active = devices.filter((d) => d.is_active && d.status !== "deprovisioned");
    if (active.some((d) => connectivityState(d.last_seen_at, now, d.is_online) === "online")) return { isOnline: true };
    // A truncated page cannot establish that every linked device is offline.
    if (!connectivity.data || (!connectivity.data.complete && devices.length < row.device_count)) return {};
    return { isOnline: false };
  }

  const invalidate = () => void invalidatePolicyViews(qc);

  const create = useMutation({
    mutationFn: () => employeesApi.create(createForm),
    onSuccess: () => {
      invalidate();
      setCreateOpen(false);
      setCreateForm(EMPTY_CREATE_FORM);
    },
  });

  const editMut = useMutation({
    mutationFn: (vars: { id: string; body: Partial<EmployeeFormState> }) =>
      employeesApi.patch(vars.id, vars.body),
    onSuccess: () => {
      invalidate();
      setEditRow(null);
    },
  });

  const forceEntry = useMutation({
    mutationFn: (id: string) => employeesApi.forceEntry(id),
    onSuccess: (data) => {
      invalidate();
      setPendingForceEntry(null);
      pushToast({
        tone: "success",
        title: formatMessage("employees.forceEntry.toast", { id: data.employee.employee_id }),
      });
    },
    onError: (err) => {
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) });
    },
  });

  const forceExit = useMutation({
    mutationFn: (id: string) => employeesApi.forceExit(id),
    onSuccess: (data) => {
      invalidate();
      setPendingForceExit(null);
      pushToast({
        tone: "success",
        title: formatMessage("employees.forceExit.toast", { id: data.employee.employee_id }),
      });
    },
    onError: (err) => {
      pushToast({ tone: "danger", title: t("common.error"), body: translateError(err) });
    },
  });

  function openEdit(row: Employee) {
    setEditRow(row);
    setEditForm({ first_name: row.first_name, last_name: row.last_name, phone: row.phone });
  }

  function employeeLabel(row: Employee): string {
    return row.full_name || row.employee_id;
  }

  const columns: Column<Employee>[] = [
    {
      key: "employee_id",
      header: t("employees.list.column.employeeId"),
      render: (row) => <span className="mono">{row.employee_id}</span>,
    },
    {
      key: "name",
      header: t("employees.list.column.name"),
      render: (row) => row.full_name || "—",
    },
    {
      key: "phone",
      header: t("employees.list.column.phone"),
      render: (row) => row.phone || "—",
    },
    {
      key: "connectivity",
      header: t("operations.connectivity.title"),
      render: (row) => row.device_count === 0
        ? <Badge tone="neutral">{t("operations.connectivity.noDevice")}</Badge>
        : <ConnectivityBadge {...employeeConnectivity(row)} now={now} />,
    },
    {
      key: "status",
      header: t("operations.employees.presence"),
      render: (row) => (
        <Badge tone={row.protection_active ? "success" : "neutral"}>
          {t(row.protection_active ? "operations.employees.present" : "operations.employees.absent")}
        </Badge>
      ),
    },
    {
      key: "last_entry_at",
      header: t("employees.list.column.lastEntry"),
      render: (row) => (
        <span className="mono table-cell--datetime">{formatDateTime(row.last_entry_at)}</span>
      ),
    },
    {
      key: "last_exit_at",
      header: t("employees.list.column.lastExit"),
      render: (row) => (
        <span className="mono table-cell--datetime">{formatDateTime(row.last_exit_at)}</span>
      ),
    },
    {
      key: "device_count",
      header: t("employees.list.column.devices"),
      render: (row) => <span className="num">{row.device_count}</span>,
      align: "right",
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
          <Button size="sm" variant="secondary" onClick={() => setHistoryRow(row)}>
            {t("employees.actions.history")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openEdit(row)}>
            {t("employees.actions.edit")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setPendingForceEntry(row)}>
            {t("employees.actions.forceEntry")}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setPendingForceExit(row)}>
            {t("employees.actions.forceExit")}
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={t("employees.list.eyebrow")}
        title={t("employees.list.title")}
        subtitle={t("employees.list.subtitle")}
        actions={
          <Button onClick={() => setCreateOpen(true)}>{t("employees.list.action.create")}</Button>
        }
      />

      <div className="filter-bar">
        <SearchInput
          placeholder={t("employees.list.searchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <p className="muted">{t("operations.employees.connectivityHint")}</p>
      {needsDeviceFallback && (!connectivity.data?.complete || connectivity.error) && (
        <Alert tone="info">{t("operations.employees.partialConnectivity")}</Alert>
      )}
      <Card padding="none">
        {query.isLoading && <LoadingState />}
        {query.error && (
          <ErrorState
            titleKey="states.errorTitle"
            message={translateError(query.error)}
            action={
              <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
                {t("common.retry")}
              </Button>
            }
          />
        )}
        {query.data && (
          <Table
            columns={columns}
            rows={query.data}
            rowKey={(row) => row.id}
            empty={
              <EmptyState titleKey="employees.list.emptyTitle" bodyKey="employees.list.emptyBody" />
            }
          />
        )}
      </Card>

      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title={t("employees.create.title")}>
        <form
          onSubmit={(e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            if (!createForm.employee_id.trim()) return;
            create.mutate();
          }}
          style={{ display: "flex", flexDirection: "column", gap: 16 }}
        >
          {create.error && <Alert tone="danger">{translateError(create.error)}</Alert>}
          <FormField
            label={t("employees.create.field.employeeId")}
            hint={t("employees.create.hint.employeeId")}
            required
          >
            {(id) => (
              <Input
                id={id}
                value={createForm.employee_id}
                onChange={(e) => setCreateForm((f) => ({ ...f, employee_id: e.target.value }))}
                placeholder={t("employees.create.placeholder.employeeId")}
                required
              />
            )}
          </FormField>
          <FormField label={t("employees.create.field.firstName")}>
            {(id) => (
              <Input
                id={id}
                value={createForm.first_name}
                onChange={(e) => setCreateForm((f) => ({ ...f, first_name: e.target.value }))}
                placeholder={t("employees.create.placeholder.firstName")}
              />
            )}
          </FormField>
          <FormField label={t("employees.create.field.lastName")}>
            {(id) => (
              <Input
                id={id}
                value={createForm.last_name}
                onChange={(e) => setCreateForm((f) => ({ ...f, last_name: e.target.value }))}
                placeholder={t("employees.create.placeholder.lastName")}
              />
            )}
          </FormField>
          <FormField label={t("employees.create.field.phone")}>
            {(id) => (
              <Input
                id={id}
                value={createForm.phone}
                onChange={(e) => setCreateForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder={t("employees.create.placeholder.phone")}
              />
            )}
          </FormField>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button variant="ghost" type="button" onClick={() => setCreateOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={create.isPending}>
              {t("employees.create.submit")}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={editRow !== null}
        onClose={() => setEditRow(null)}
        title={editRow ? formatMessage("employees.edit.title", { name: employeeLabel(editRow) }) : ""}
      >
        {editRow && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              editMut.mutate({ id: editRow.id, body: editForm });
            }}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            {editMut.error && <Alert tone="danger">{translateError(editMut.error)}</Alert>}
            <FormField label={t("employees.create.field.firstName")}>
              {(id) => (
                <Input
                  id={id}
                  value={editForm.first_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, first_name: e.target.value }))}
                />
              )}
            </FormField>
            <FormField label={t("employees.create.field.lastName")}>
              {(id) => (
                <Input
                  id={id}
                  value={editForm.last_name}
                  onChange={(e) => setEditForm((f) => ({ ...f, last_name: e.target.value }))}
                />
              )}
            </FormField>
            <FormField label={t("employees.create.field.phone")}>
              {(id) => (
                <Input
                  id={id}
                  value={editForm.phone}
                  onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
                />
              )}
            </FormField>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <Button variant="ghost" type="button" onClick={() => setEditRow(null)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" loading={editMut.isPending}>
                {t("employees.edit.submit")}
              </Button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingForceEntry !== null}
        title={
          pendingForceEntry
            ? formatMessage("employees.forceEntry.confirmTitle", {
                name: employeeLabel(pendingForceEntry),
              })
            : ""
        }
        body={t("employees.forceEntry.confirmBody")}
        tone="primary"
        loading={forceEntry.isPending}
        onCancel={() => setPendingForceEntry(null)}
        onConfirm={() => {
          if (pendingForceEntry) forceEntry.mutate(pendingForceEntry.id);
        }}
      />

      <ConfirmDialog
        open={pendingForceExit !== null}
        title={
          pendingForceExit
            ? formatMessage("employees.forceExit.confirmTitle", {
                name: employeeLabel(pendingForceExit),
              })
            : ""
        }
        body={t("employees.forceExit.confirmBody")}
        tone="primary"
        loading={forceExit.isPending}
        onCancel={() => setPendingForceExit(null)}
        onConfirm={() => {
          if (pendingForceExit) forceExit.mutate(pendingForceExit.id);
        }}
      />

      <Modal
        open={historyRow !== null}
        onClose={() => setHistoryRow(null)}
        size="lg"
        title={
          historyRow
            ? formatMessage("employees.history.title", { name: employeeLabel(historyRow) })
            : ""
        }
      >
        {historyRow && <EmployeeHistoryPanel employee={historyRow} />}
      </Modal>
    </div>
  );
}

function EmployeeHistoryPanel({ employee }: { employee: Employee }) {
  const query = useQuery({
    queryKey: ["employees", employee.id, "events"],
    queryFn: () => employeesApi.events(employee.id),
    refetchInterval: LIVE_REFRESH_INTERVAL_MS,
  });

  const columns: Column<PresenceEvent>[] = [
    {
      key: "event_type",
      header: t("employees.history.column.eventType"),
      render: (row) => (
        <Badge tone={row.event_type === "entry" ? "success" : "neutral"}>
          {t(`employees.history.eventType.${row.event_type}`)}
        </Badge>
      ),
    },
    {
      key: "occurred_at",
      header: t("employees.history.column.occurredAt"),
      render: (row) => (
        <span className="mono table-cell--datetime">{formatDateTime(row.occurred_at)}</span>
      ),
    },
    {
      key: "source",
      header: t("employees.history.column.source"),
      render: (row) => sourceLabel(row.source),
    },
    {
      key: "applied",
      header: t("employees.history.column.applied"),
      render: (row) => (
        <div>
          <Badge tone={row.applied ? "success" : "warning"}>
            {row.applied ? t("employees.history.applied.yes") : t("employees.history.applied.no")}
          </Badge>
          {!row.applied && row.notes && (
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 4 }}>
              {t(`employees.history.notes.${row.notes}`, row.notes)}
            </div>
          )}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="kv-grid" style={{ borderRadius: 8, marginBottom: 16 }}>
        <div className="kv">
          <div className="kv__label">{t("common.status")}</div>
          <div className="kv__value">
            <Badge tone={employee.protection_active ? "success" : "neutral"}>
              {employee.protection_active ? t("common.active") : t("common.inactive")}
            </Badge>
          </div>
        </div>
        <div className="kv">
          <div className="kv__label">{t("employees.history.summary.lastEventSource")}</div>
          <div className="kv__value">{sourceLabel(employee.last_event_source)}</div>
        </div>
        <div className="kv">
          <div className="kv__label">{t("employees.list.column.devices")}</div>
          <div className="kv__value">{employee.device_count}</div>
        </div>
      </div>

      {query.isLoading && <LoadingState />}
      {query.error && <ErrorState titleKey="states.errorTitle" message={translateError(query.error)} />}
      {query.data && (
        <Table
          columns={columns}
          rows={query.data}
          rowKey={(row) => row.id}
          empty={
            <EmptyState titleKey="employees.history.emptyTitle" bodyKey="employees.history.emptyBody" />
          }
        />
      )}
    </div>
  );
}
